import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from 'react'

import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'

type ToastTone = 'success' | 'error' | 'info'

type ToastItem = {
    id: number
    tone: ToastTone
    title: string
    description?: string
}

type ToastApi = {
    success: (title: string, description?: string) => void
    error: (title: string, description?: string) => void
    info: (title: string, description?: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const TONES: Record<ToastTone, { icon: typeof CircleCheck, className: string }> = {
    success: { icon: CircleCheck, className: 'bg-success/12 text-success' },
    error: { icon: CircleAlert, className: 'bg-destructive/12 text-destructive' },
    info: { icon: Info, className: 'bg-primary/12 text-primary' }
}

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [items, setItems] = useState<ToastItem[]>([])
    const counter = useRef(0)

    const dismiss = useCallback((id: number): void => {
        setItems(list => list.filter(item => item.id !== id))
    }, [])

    const push = useCallback((tone: ToastTone, title: string, description?: string): void => {
        counter.current += 1
        const id = counter.current

        setItems(list => [...list.slice(-2), { id, tone, title, description }])
        window.setTimeout(() => dismiss(id), tone === 'error' ? 6000 : 4200)
    }, [dismiss])

    const api = useMemo<ToastApi>(() => ({
        success: (title, description) => push('success', title, description),
        error: (title, description) => push('error', title, description),
        info: (title, description) => push('info', title, description)
    }), [push])

    return (
        <ToastContext.Provider value={api}>
            {children}

            <div
                role='status'
                aria-live='polite'
                className='pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6 print:hidden'
            >
                {items.map(item => {
                    const Icon = TONES[item.tone].icon

                    return (
                        <div
                            key={item.id}
                            className='pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border bg-popover p-3.5 pr-2.5 text-popover-foreground shadow-float animate-in fade-in-0 slide-in-from-bottom-3 duration-300'
                        >
                            <span className={cn('grid size-8 shrink-0 place-items-center rounded-full', TONES[item.tone].className)} aria-hidden='true'>
                                <Icon className='size-4' />
                            </span>

                            <div className='min-w-0 flex-1 pt-0.5'>
                                <p className='text-sm leading-snug font-semibold'>
                                    {item.title}
                                </p>

                                {item.description && (
                                    <p className='mt-0.5 text-[13px] leading-relaxed text-muted-foreground'>
                                        {item.description}
                                    </p>
                                )}
                            </div>

                            <button
                                type='button'
                                onClick={() => dismiss(item.id)}
                                className='grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/40'
                                aria-label='Tutup notifikasi'
                            >
                                <X className='size-4' aria-hidden='true' />
                            </button>
                        </div>
                    )
                })}
            </div>
        </ToastContext.Provider>
    )
}

export const useToast = (): ToastApi => {
    const context = useContext(ToastContext)

    if (!context) throw new Error('useToast must be used inside ToastProvider')

    return context
}
